import {
  Children,
  ComponentType,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import { Tooltip, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useSidebarOpenState } from '@backstage/core-components';

const STORAGE_KEY = 'fluxstage.sidebar.collapsedSections.v2';

export const useNavStyles = makeStyles(theme => {
  const dark = theme.palette.type === 'dark';
  const accent = dark ? '#b794f6' : '#6e3a8f';
  return {
    section: {
      width: '100%',
      marginTop: theme.spacing(1),
      flexShrink: 0,
    },
    sectionHeader: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 10px 6px 14px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: dark ? '#7d8597' : '#8a94a6',
      borderRadius: 6,
      '&:hover': { color: dark ? '#c3c9d5' : '#454f5b' },
    },
    sectionCount: {
      marginLeft: 6,
      fontWeight: 500,
      opacity: 0.7,
    },
    chevron: {
      fontSize: '16px !important',
      transition: 'transform 0.2s ease',
    },
    chevronCollapsed: { transform: 'rotate(-90deg)' },
    sectionBody: {
      overflow: 'hidden',
      maxHeight: 600,
      transition: 'max-height 0.25s ease',
    },
    sectionBodyCollapsed: { maxHeight: 0 },
    railDivider: {
      height: 1,
      width: 18,
      margin: '8px auto 7px',
      background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    },
    railSection: {
      marginTop: 0,
      '&:first-child $railDivider': { display: 'none' },
    },
    railBody: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      overflow: 'visible',
    },
    item: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      height: 32,
      margin: '1px 8px',
      padding: '0 10px',
      borderRadius: 8,
      border: '1px solid transparent',
      color: dark ? '#cfd5df' : '#3a4452',
      textDecoration: 'none',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      background: 'none',
      fontFamily: 'inherit',
      width: 'calc(100% - 16px)',
      boxSizing: 'border-box',
      transition:
        'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        backgroundColor: dark
          ? 'rgba(255,255,255,0.05)'
          : 'rgba(110,58,143,0.06)',
        color: dark ? '#ffffff' : '#212b36',
      },
      '&:hover $icon': { color: dark ? '#e2e6ee' : '#454f5b' },
    },
    itemRail: {
      display: 'flex !important',
      flex: '0 0 38px',
      justifyContent: 'center',
      padding: 0,
      minWidth: 38,
      maxWidth: 38,
      height: 38,
      margin: 0,
      boxSizing: 'border-box',
      borderRadius: 10,
      borderColor: dark ? 'rgba(255,255,255,.035)' : 'rgba(17,24,39,.045)',
      backgroundColor: dark ? 'rgba(255,255,255,.018)' : 'rgba(17,24,39,.018)',
      '&:hover': {
        transform: 'translateX(2px)',
        borderColor: dark ? 'rgba(183,148,246,.28)' : 'rgba(110,58,143,.2)',
        backgroundColor: dark ? 'rgba(139,92,246,.1)' : 'rgba(110,58,143,.06)',
      },
    },
    itemActive: {
      backgroundColor: dark ? 'rgba(139,92,246,0.14)' : 'rgba(110,58,143,0.08)',
      borderColor: dark ? 'rgba(139,92,246,0.55)' : 'rgba(110,58,143,0.35)',
      color: dark ? '#ffffff' : '#552569',
      fontWeight: 600,
      boxShadow: dark ? '0 0 0 3px rgba(139,92,246,0.08)' : 'none',
      '& $icon': { color: accent },
      '&:hover': {
        backgroundColor: dark
          ? 'rgba(139,92,246,0.18)'
          : 'rgba(110,58,143,0.1)',
      },
      '&$itemRail': {
        borderColor: dark ? 'rgba(139,92,246,.6)' : 'rgba(110,58,143,.4)',
        boxShadow: dark
          ? 'inset 3px 0 0 #a78bfa, 0 4px 12px rgba(0,0,0,.16)'
          : 'inset 3px 0 0 #6e3a8f',
      },
    },
    itemDashed: {
      borderStyle: 'dashed',
      borderColor: dark ? 'rgba(255,255,255,0.14)' : 'rgba(110,58,143,0.3)',
      color: accent,
      '& $icon': { color: accent },
      '&:hover': { color: accent },
    },
    icon: {
      flexShrink: 0,
      width: 18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: dark ? '#8f98aa' : '#6b7685',
      '& svg': { fontSize: 18 },
    },
    iconRail: {
      width: 20,
      color: dark ? '#9ba5b8' : '#667384',
      '& svg': { fontSize: 19 },
    },
    label: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    badge: {
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      height: 18,
      padding: '0 6px',
      borderRadius: 6,
      fontSize: 10.5,
      fontWeight: 600,
      lineHeight: 1,
    },
    badgeMono: {
      fontFamily:
        '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      color: accent,
      background: dark ? 'rgba(139,92,246,0.16)' : 'rgba(110,58,143,0.1)',
      border: `1px solid ${
        dark ? 'rgba(139,92,246,0.35)' : 'rgba(110,58,143,0.2)'
      }`,
    },
    badgeLive: {
      color: dark ? '#6ee7b7' : '#0b7a4b',
      background: dark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.1)',
      border: `1px solid ${
        dark ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.3)'
      }`,
    },
    badgeWarn: {
      color: dark ? '#fcd34d' : '#8a5a00',
      background: dark ? 'rgba(245,158,11,0.14)' : 'rgba(245,158,11,0.14)',
      border: `1px solid ${
        dark ? 'rgba(245,158,11,0.4)' : 'rgba(245,158,11,0.35)'
      }`,
    },
    badgeMuted: {
      color: dark ? '#9aa3b5' : '#637381',
      background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    },
    badgeDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: 'currentColor',
    },
    dotOnly: {
      flexShrink: 0,
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: '#f5b82e',
      boxShadow: '0 0 0 3px rgba(245,184,46,0.18)',
    },
    railDot: {
      position: 'absolute',
      top: 6,
      right: 6,
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: '#f5b82e',
    },
    httpIcon: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: 7.5,
      fontWeight: 700,
      letterSpacing: '0.02em',
      padding: '2px 3px',
      borderRadius: 4,
      border: '1px solid currentColor',
      lineHeight: 1,
    },
  };
});

export type NavBadge =
  | { kind: 'mono' | 'live' | 'warn' | 'muted'; label: string; dot?: boolean }
  | { kind: 'dot' };

export const NavBadgeView = ({ badge }: { badge: NavBadge }) => {
  const classes = useNavStyles();
  if (badge.kind === 'dot') return <span className={classes.dotOnly} />;
  const kindClass = {
    mono: classes.badgeMono,
    live: classes.badgeLive,
    warn: classes.badgeWarn,
    muted: classes.badgeMuted,
  }[badge.kind];
  return (
    <span className={`${classes.badge} ${kindClass}`}>
      {badge.dot && <span className={classes.badgeDot} />}
      {badge.label}
    </span>
  );
};

export const HttpIcon = () => {
  const classes = useNavStyles();
  return <span className={classes.httpIcon}>HTTP</span>;
};

type NavItemProps = {
  icon: ComponentType<any>;
  text: string;
  to?: string;
  end?: boolean;
  onClick?: () => void;
  badge?: NavBadge;
  dashed?: boolean;
};

export const NavItem = ({
  icon: Icon,
  text,
  to,
  end,
  onClick,
  badge,
  dashed,
}: NavItemProps) => {
  const classes = useNavStyles();
  const { isOpen } = useSidebarOpenState();

  const base = `${classes.item} ${isOpen ? '' : classes.itemRail} ${
    dashed ? classes.itemDashed : ''
  }`;
  const content: ReactNode = (
    <>
      <span className={`${classes.icon} ${isOpen ? '' : classes.iconRail}`}>
        <Icon />
      </span>
      {isOpen && <span className={classes.label}>{text}</span>}
      {isOpen && badge && <NavBadgeView badge={badge} />}
      {!isOpen && badge && badge.kind !== 'muted' && badge.kind !== 'mono' && (
        <span className={classes.railDot} />
      )}
    </>
  );

  const element = to ? (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        `${base} ${isActive && !dashed ? classes.itemActive : ''}`
      }
    >
      {content}
    </NavLink>
  ) : (
    <button type="button" className={base} onClick={onClick}>
      {content}
    </button>
  );

  if (isOpen) return element;
  return (
    <Tooltip title={text} placement="right" arrow>
      {element}
    </Tooltip>
  );
};

function readCollapsed(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export const NavSection = ({
  title,
  children,
}: PropsWithChildren<{ title: string }>) => {
  const classes = useNavStyles();
  const { isOpen } = useSidebarOpenState();
  const [collapsed, setCollapsed] = useState(() => readCollapsed().has(title));

  useEffect(() => {
    const stored = readCollapsed();
    if (collapsed) stored.add(title);
    else stored.delete(title);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...stored]));
    } catch {
      // ignore
    }
  }, [collapsed, title]);

  const count = Children.toArray(children).filter(Boolean).length;

  if (!isOpen) {
    return (
      <div className={`${classes.section} ${classes.railSection}`}>
        <div className={classes.railDivider} />
        <div className={classes.railBody}>{children}</div>
      </div>
    );
  }

  return (
    <div className={classes.section}>
      <button
        type="button"
        className={classes.sectionHeader}
        onClick={() => setCollapsed(prev => !prev)}
        aria-expanded={!collapsed}
      >
        <span>
          {title}
          {count > 1 && <span className={classes.sectionCount}>({count})</span>}
        </span>
        <ExpandMoreIcon
          className={`${classes.chevron} ${
            collapsed ? classes.chevronCollapsed : ''
          }`}
        />
      </button>
      <div
        className={`${classes.sectionBody} ${
          collapsed ? classes.sectionBodyCollapsed : ''
        }`}
        aria-hidden={collapsed}
      >
        {children}
      </div>
    </div>
  );
};
